<template>
  <ul
    ref="linkList"
    class="-mt-3 max-h-[80dvh] list-none space-y-2 divide-y-2 overflow-y-auto pb-3 md:max-h-[35rem]"
  >
    <li
      v-for="(link, index) in selectedLinks"
      :key="
        'id' in link ? link.id : `empty-${link.legal_text_paragraph_linked_id}`
      "
    >
      <LinkSelection
        :modelValue="link"
        :legalText="legalText"
        :paragraph="paragraph"
        :linkTypes="linkTypes"
        :allowDelete="!!selectedLinks && selectedLinks.length > 1"
        @update:modelValue="handleUpdate(index, $event)"
        @delete="handleDelete(index)"
      />
    </li>
  </ul>
</template>

<script lang="ts">
export const getEmptyLink = () => ({
  additional_text: "",
  link_type_id: undefined,
  type: "link",
  legal_text_paragraph_linked: undefined,
  legal_text_paragraph_linked_id: undefined,
});
</script>

<script lang="ts" setup>
import { LinkClient } from "~/api/link";
import type { ILink, INewLink, IUpdatableLinkChain } from "~/models/IUserEntry";
import type { ISelect } from "~/models/ISelect";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

defineProps<{
  saving: boolean;
  legalText: ILegalText;
  paragraph: ILegalTextParagraph;
}>();

const selectedLinks = defineModel<IUpdatableLinkChain["links"]>();

onBeforeMount(() => {
  if (!selectedLinks.value) {
    selectedLinks.value = [getEmptyLink()];
  }
});

const linkList = ref<HTMLElement>();
const linkClient = useApiClient(LinkClient);

const linkTypes = ref<ISelect[]>([]);

onMounted(async () => {
  const types = await linkClient.fetchLinkTypes();
  linkTypes.value = types.map((type) => ({
    label: type.name,
    value: type.id,
  }));
});

const scrollToBottom = async () => {
  await nextTick();
  const list = unrefElement(linkList);
  if (list) {
    list.scroll({
      top: list.scrollHeight - list.clientHeight,
      behavior: "smooth",
    });
  }
};

const handleAdd = () => {
  if (!selectedLinks.value) {
    selectedLinks.value = [];
  }
  selectedLinks.value.push(getEmptyLink());
  return scrollToBottom();
};

const resetLinks = () => {
  selectedLinks.value = [getEmptyLink()];
};

defineExpose({
  handleAdd,
  resetLinks,
});

const handleUpdate = (index: number, link: INewLink | ILink) => {
  selectedLinks.value?.splice(index, 1, link);
};

const handleDelete = (index: number) => {
  selectedLinks.value?.splice(index, 1);
};
</script>
